import {
  Button,
  FloorbitLogo,
  IndeterminateProgress,
} from "@facade/components/ui";

import { CircleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

type IfcViewerSplashProps = {
  title?: string;
  description?: string;
  message?: string;
  progress?: number;
  progressLabel?: string;
  error?: boolean;
  onBack?: () => void;
  onCancel?: () => void;
};

export function IfcViewerSplash({
  title,
  description,
  message,
  progress,
  progressLabel,
  error = false,
  onBack,
  onCancel,
}: IfcViewerSplashProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("viewer.loading.modulesTitle");
  const resolvedDescription =
    description ?? t("viewer.loading.modulesDescription");

  return (
    <div className="relative grid h-dvh w-screen place-items-center overflow-hidden bg-background p-6 text-foreground">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        <div
          className={
            error
              ? "mb-6 grid size-16 place-items-center"
              : "grid size-16 place-items-center"
          }
        >
          {error ? (
            <CircleAlert className="size-7 text-destructive" />
          ) : (
            <FloorbitLogo animated size="md" />
          )}
        </div>

        {error ? (
          <>
            <h1 className="text-xl font-semibold tracking-tight">
              {resolvedTitle}
            </h1>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {resolvedDescription}
            </p>
            {onBack ? (
              <Button className="mt-8" onClick={onBack}>
                {t("viewer.actions.back")}
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <p className="mt-4 text-sm text-muted-foreground">
              {message ?? t("viewer.loading.modulesLabel")}
            </p>
            <div className="mt-4 w-48 max-w-full">
              {progress === undefined ? (
                <IndeterminateProgress variant="neutral" />
              ) : (
                <div
                  className="h-1 overflow-hidden rounded-full bg-accent"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                >
                  <div
                    className="h-full rounded-full bg-accent-foreground/70 transition-[width] duration-200"
                    style={{ width: `${Math.max(2, progress)}%` }}
                  />
                </div>
              )}
              {progressLabel ? (
                <div
                  className="mt-2 text-xs tabular-nums text-muted-foreground"
                  aria-live="polite"
                >
                  {progressLabel}
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
      {!error && onCancel ? (
        <Button
          type="button"
          variant="outline"
          className="absolute bottom-32 left-1/2 w-24 -translate-x-1/2"
          onClick={onCancel}
        >
          {t("actions.cancel")}
        </Button>
      ) : null}
    </div>
  );
}
